import { API_BASE_URL, API_ENDPOINTS } from '../config/api';
import { api } from '../utils/apiClient';
import type {
  Ticket,
  TicketMessage,
  CreateTicketRequest,
  UpdateTicketRequest,
  AddMessageRequest,
  GetTicketsResponse,
  GetTicketResponse,
  CreateTicketResponse,
  GetMessagesResponse,
  AddMessageResponse,
} from '../types/ticket';

const BASE_URL = API_BASE_URL.CAMPAIGN; // Using campaign service base URL

/**
 * Create a new support ticket
 */
export const createTicket = async (data: CreateTicketRequest): Promise<CreateTicketResponse> => {
  const url = `${BASE_URL}${API_ENDPOINTS.TICKET.CREATE}`;
  return await api.post<CreateTicketResponse>(url, data);
};

/**
 * Get all tickets for the authenticated user
 */
export const getTickets = async (
  page: number = 1,
  pageSize: number = 10
): Promise<GetTicketsResponse> => {
  const url = `${BASE_URL}${API_ENDPOINTS.TICKET.LIST}?page=${page}&page_size=${pageSize}`;
  return await api.get<GetTicketsResponse>(url);
};

/**
 * Get a specific ticket by ID
 */
export const getTicketById = async (ticketId: string): Promise<GetTicketResponse> => {
  const url = `${BASE_URL}${API_ENDPOINTS.TICKET.GET}/${ticketId}`;
  return await api.get<GetTicketResponse>(url);
};

/**
 * Get a ticket by ticket number (e.g., TCK-002)
 */
export const getTicketByNumber = async (ticketNumber: string): Promise<GetTicketResponse> => {
  const url = `${BASE_URL}${API_ENDPOINTS.TICKET.GET_BY_NUMBER}/${ticketNumber}`;
  return await api.get<GetTicketResponse>(url);
};

/**
 * Update ticket details
 */
export const updateTicket = async (
  ticketId: string,
  data: UpdateTicketRequest
): Promise<GetTicketResponse> => {
  const url = `${BASE_URL}${API_ENDPOINTS.TICKET.UPDATE}/${ticketId}`;
  return await api.put<GetTicketResponse>(url, data);
};

/**
 * Close a ticket (marks it as resolved)
 */
export const closeTicket = async (ticketId: string): Promise<GetTicketResponse> => {
  const url = `${BASE_URL}${API_ENDPOINTS.TICKET.CLOSE}/${ticketId}/close`;
  return await api.post<GetTicketResponse>(url);
};

/**
 * Delete a ticket (soft delete)
 */
export const deleteTicket = async (ticketId: string): Promise<{ success: boolean; message?: string }> => {
  const url = `${BASE_URL}${API_ENDPOINTS.TICKET.DELETE}/${ticketId}`;
  return await api.delete<{ success: boolean; message?: string }>(url);
};

/**
 * Add a message/reply to a ticket conversation
 */
export const addMessageToTicket = async (
  ticketId: string,
  data: AddMessageRequest
): Promise<AddMessageResponse> => {
  const url = `${BASE_URL}${API_ENDPOINTS.TICKET.ADD_MESSAGE}/${ticketId}/messages`;
  return await api.post<AddMessageResponse>(url, data);
};

/**
 * Get all messages in a ticket conversation
 */
export const getTicketMessages = async (ticketId: string): Promise<GetMessagesResponse> => {
  const url = `${BASE_URL}${API_ENDPOINTS.TICKET.GET_MESSAGES}/${ticketId}/messages`;
  return await api.get<GetMessagesResponse>(url);
};

